import React, { useContext } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ToastContext } from '@components/auth/ToastContext.tsx';
import SvgLink from '@icons/link.svg?react';
import SvgCoCopy from '@icons/CoCopy.tsx';

interface ComponentProps {
  locationId: string;
  dataTestId?: string;
}

// Read-only chip (copy affordance only, no popover editor) — visual style mirrors ButtonTag's "defined" variant.
const LocationTvLinkTag: React.FC<ComponentProps> = ({ locationId, dataTestId }) => {
  const { tr } = useTranslationLgs();
  const { showToastMessage } = useContext(ToastContext);

  // Copy-only link (no in-app navigation), so we build the bare URL the TV panel expects —
  // no lang segment, no runAs. window.location.origin covers local/alpha/playground/prod.
  const url = `${window.location.origin}/location/location-panel/${locationId}`;

  const copy = () => {
    navigator.clipboard.writeText(url).then(() => {
      showToastMessage(tr('LocalityDetail.tvLinkCopied', 'Link copied to clipboard'), url, 'success');
    });
  };

  return (
    <div className="inline-flex rounded-lg px-3 py-1.5 gap-3 items-center content-center border border-solid border-content-brandMedium bg-white" data-testid={dataTestId}>
      <div className="text-content-secondary">
        <SvgLink className="w-4 h-4" />
      </div>
      <div className="flex flex-col">
        <div className="text-content-secondary text-xs leading-[1.33333]">{tr('LocalityDetail.tvLink', 'Link to internal TV')}</div>
        <div className="font-medium text-sm leading-[1.28571429]" data-testid={dataTestId ? `${dataTestId}Value` : undefined}>
          {url}
        </div>
      </div>
      <button
        type="button"
        className="cursor-pointer border-none bg-transparent p-0 text-content-brand hover:text-complementary-brandDark"
        onClick={copy}
        title={tr('generic.copy', 'Copy')}
        data-testid={dataTestId ? `${dataTestId}Copy` : undefined}
      >
        <SvgCoCopy />
      </button>
    </div>
  );
};

export default LocationTvLinkTag;
